import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Button,
  Typography,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Stack,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Email as EmailIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { doc, getDoc, setDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../shared/utils/firebase';
import { managedLocations } from '../../auth/managedLocations';
import { LoadingSpinner, InlineLoadingSpinner } from '../../../shared/components';

interface Manager {
  email: string;
  name: string;
  role: 'MANAGER' | 'ADMIN';
  assignedLocation?: string;
}

const EmailRecipientManager: React.FC = () => {
  const [recipients, setRecipients] = useState<string[]>([]);
  const [availableManagers, setAvailableManagers] = useState<Manager[]>([]);
  const [newEmail, setNewEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch current email config
      const configDoc = await getDoc(doc(db, 'systemSettings', 'emailConfig'));
      if (configDoc.exists()) {
        const data = configDoc.data();
        if (data.weeklyManagerSummaryRecipients) {
          setRecipients(data.weeklyManagerSummaryRecipients);
        }
      }

      // Fetch all managers and admins
      const usersQuery = query(
        collection(db, 'users'),
        where('role', 'in', ['MANAGER', 'ADMIN'])
      );
      const usersSnapshot = await getDocs(usersQuery);

      const managers: Manager[] = [];
      usersSnapshot.forEach(doc => {
        const userData = doc.data();
        if (userData.email) {
          managers.push({
            email: userData.email,
            name: userData.name || userData.displayName || userData.email.split('@')[0],
            role: userData.role,
            assignedLocation: managedLocations(userData).join(' + ') || undefined
          });
        }
      });

      setAvailableManagers(managers.sort((a, b) => a.email.localeCompare(b.email)));
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load email recipients');
    } finally {
      setLoading(false);
    }
  };

  const validateEmail = (email: string): boolean => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const addRecipient = (email: string) => {
    if (!email) return;
    
    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (recipients.includes(email)) {
      setError('This email is already in the list');
      return;
    }

    setRecipients([...recipients, email]);
    setNewEmail('');
    setError(null);
    setSuccess(null);
  };

  const removeRecipient = (email: string) => {
    setRecipients(recipients.filter(r => r !== email));
    setSuccess(null);
  };

  const saveRecipients = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      await setDoc(doc(db, 'systemSettings', 'emailConfig'), {
        weeklyManagerSummaryRecipients: recipients,
        updatedAt: new Date().toISOString()
      }, { merge: true });

      setSuccess(`Email recipient list saved successfully. ${recipients.length} recipients will receive the weekly summary.`);
    } catch (error) {
      console.error('Error saving recipients:', error);
      setError('Failed to save email recipients');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <LoadingSpinner minHeight="200px" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        avatar={<EmailIcon />}
        title="Weekly Manager Summary Recipients"
        subheader="Configure which managers receive the weekly summary email"
      />
      <CardContent>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Add email addresses to control who receives the weekly manager summary email every Tuesday at 8:00 AM.
            If no recipients are configured, all managers and admins will receive the email by default.
          </Typography>
        </Alert>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Quick Add from Active Managers
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
            {availableManagers
              .filter(m => !recipients.includes(m.email))
              .map(manager => (
                <Chip
                  key={manager.email}
                  label={`${manager.name} (${manager.role}${manager.assignedLocation ? ` - ${manager.assignedLocation}` : ''})`}
                  onClick={() => addRecipient(manager.email)}
                  icon={<AddIcon />}
                  color={manager.role === 'ADMIN' ? 'secondary' : 'primary'}
                  size="small"
                />
              ))}
          </Stack>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Add Custom Email
          </Typography>
          <Stack direction="row" spacing={2}>
            <TextField
              fullWidth
              placeholder="Enter email address"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addRecipient(newEmail)}
              error={!!error && error.includes('email')}
              size="small"
            />
            <Button
              variant="contained"
              onClick={() => addRecipient(newEmail)}
              disabled={!newEmail}
              startIcon={<AddIcon />}
            >
              Add
            </Button>
          </Stack>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Current Recipients ({recipients.length})
          </Typography>
          {recipients.length === 0 ? (
            <Alert severity="warning">
              No recipients configured. All managers and admins will receive emails by default.
            </Alert>
          ) : (
            <List>
              {recipients.map(email => {
                const manager = availableManagers.find(m => m.email === email);
                return (
                  <ListItem key={email} divider>
                    <ListItemText
                      primary={email}
                      secondary={manager ? `${manager.name} - ${manager.role}${manager.assignedLocation ? ` (${manager.assignedLocation})` : ''}` : 'Custom recipient'}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => removeRecipient(email)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
            </List>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={fetchData}
            disabled={saving}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={saveRecipients}
            disabled={saving}
            startIcon={saving ? <InlineLoadingSpinner size={20} /> : <SaveIcon />}
          >
            {saving ? 'Saving...' : 'Save Recipients'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default EmailRecipientManager;